import type {PropsWithChildren} from 'react'
import {createContext, useContext, useMemo} from 'react'
import {matchPath} from '@github-ui/react-router'

import {parseCommitRange} from '../utils/parse-commit-range'
import type {CommitsSelectorCommitData} from '../page-data/payloads/file-tree'
import {ChangesRoutePath} from '../routes/route-paths'

interface SelectedRefContextData {
  startOid?: string | null
  endOid?: string | null
  baseRefOid?: string
}

export const SelectedRefContext = createContext<SelectedRefContextData>({})

export function SelectedRefContextProvider({
  baseRefOid,
  path,
  commits,
  children,
}: PropsWithChildren<{baseRefOid?: string; path: string; commits?: CommitsSelectorCommitData[]}>) {
  const value = useMemo(() => {
    const isChangesRoute = matchPath(`${ChangesRoutePath}/*`, path) !== null
    const {startOid, endOid} = isChangesRoute
      ? getStartEndOidFromPathForChanges(path, baseRefOid, commits)
      : getStartEndOidFromPath(path, baseRefOid)

    return {
      endOid,
      startOid,
      baseRefOid,
    }
  }, [baseRefOid, path, commits])

  return <SelectedRefContext.Provider value={value}>{children}</SelectedRefContext.Provider>
}

export const useSelectedRefContext = () => useContext(SelectedRefContext)

export function useHasCommitRange() {
  const {endOid, startOid} = useSelectedRefContext()
  return !!endOid || !!startOid
}

function getStartEndOidFromPath(path: string, baseRefOid?: string) {
  const pathParts = path.split('/')
  const last = pathParts[pathParts.length - 1]

  let startOid: string | undefined
  let endOid: string | undefined
  if (last) {
    const pathCommitData = parseCommitRange(last)
    if (pathCommitData) {
      startOid = 'startOid' in pathCommitData ? pathCommitData.startOid : baseRefOid
      endOid = 'endOid' in pathCommitData ? pathCommitData.endOid : pathCommitData.singleCommitOid
    }
  }

  return {startOid, endOid}
}

function getStartEndOidFromPathForChanges(path: string, baseRefOid?: string, commits?: CommitsSelectorCommitData[]) {
  const pathParts = path.split('/')
  const last = pathParts[pathParts.length - 1]

  let startOid: string | undefined
  let endOid: string | undefined
  if (last) {
    const pathCommitData = parseCommitRange(last)
    if (pathCommitData) {
      if ('startOid' in pathCommitData) {
        // Explicit range: startOid..endOid
        startOid = pathCommitData.startOid
        endOid = pathCommitData.endOid
      } else {
        // Single commit: find the previous commit to use as startOid
        const singleCommitOid = pathCommitData.singleCommitOid
        endOid = singleCommitOid

        if (commits) {
          const commitIndex = commits.findIndex(commit => commit.oid === singleCommitOid)
          if (commitIndex > 0) {
            // Use the previous commit as the base
            startOid = commits[commitIndex - 1]?.oid
          } else if (commitIndex === 0) {
            // First commit in the list, use the PR base ref
            startOid = baseRefOid
          }
        }

        // If we didn't find a startOid (commits undefined, empty, or commit not found), fall back to base ref
        // future enhancements coming to seed the selected comparison data from the backend will eliminate this fallback
        if (!startOid) {
          startOid = baseRefOid
        }
      }
    }
  }

  return {startOid, endOid}
}

try{ SelectedRefContext.displayName ||= 'SelectedRefContext' } catch {}
try{ SelectedRefContextProvider.displayName ||= 'SelectedRefContextProvider' } catch {}